import { useMemo } from 'react';
import { Pressable, Text, View } from 'react-native';
import type {
  EffortScale,
  MaxForProposal,
  WorkoutExerciseEntry,
} from '@heraion/shared';
import { Button } from '../primitives/Button';
import { DisclosureCard } from '../primitives/DisclosureCard';

import type { useTheme } from './theme';
import type { WeightUnit } from './state/loggerDraft';
import { useExerciseAgePulseEnabled } from './state/exerciseAgePulsePreference';
import { useRoundingContext } from './state/plateConfig';
import { useExerciseHistoryView } from './useExerciseHistoryView';
import { rowsFromSessions, type HistorySetRow } from './historySetRows';
import { HistorySetLine } from './_HistorySetLine';
import { HistoryCopyRow } from './_HistoryCopyRow';
import { EstimateBlock } from './_EstimateBlock';
import { HistoryWidgetHeader } from './_HistoryWidgetHeader';
import { WeightCandidateSection } from './_WeightCandidateSection';
import type { WeightCandidateRow } from './weightCandidateRows';
import { makeStyles, type HistoryPanelStyles } from './HistoryPanel.styles';

// The widget of "what this athlete has actually done on this exercise", and
// since LG-59 also of "where the number in the cell came from".
//
// ⭐ Three sections, his shape (2026-09-02, `exercise-level-and-proposal.md`
// §12.2), each collapsible so the whole thing stays compact:
//   1. THE COMPUTED WEIGHTS — the one in use on top and marked, the other
//      instruments below it. This is where the provenance of a proposed load
//      lives now; it used to be a glyph beside the KG label, and his ruling
//      was that a label saying the unit should say the unit.
//   2. THIS EXERCISE'S HISTORY — the sets, not the sessions.
//   3. SIMILAR EXERCISES — restored. It existed, but only in the branch that
//      ran when the exercise had NO history of its own, so in practice nobody
//      ever saw it again after their first logged set.
// Only the section holding the value in use opens by itself.
//
// ⭐ And ONE row shape everywhere (`HistoryCopyRow`): content + «Copia», where
// Copia always copies THAT set into the set in hand. The per-row button was
// removed on 2026-09-01 by the LG-49 rewrite without anyone asking; this is
// it coming back, with the semantics he chose for it.
//
// ⭐ LG-49 still governs WHEN this may say there is nothing: the rolling
// window is not the only door, and the empty state waits for a real answer
// ([[feedback_empty_vs_unresolved_collection]]).

type Theme = ReturnType<typeof useTheme>;

/** What «Copia» hands back: one past set, to land on the set in hand.
 *  ⛔ Non esportato: i due mount lo raggiungono via `ComponentProps<typeof
 *  HistoryPanel>`, quindi un export sarebbe una seconda porta sullo stesso
 *  tipo — e `pnpm deadcode` l'ha vista subito. */
type HistoryCopyPatch = {
  weightKg: number | null;
  reps?: number | null;
};

type Props = {
  exerciseId: string;
  /** Copy a whole past SESSION into the current entry (the footer action).
   *  Absent on the exercise card, where there is no entry to copy into. */
  onCopy?: (sourceEntry: WorkoutExerciseEntry) => void;
  /** Copy ONE set into the set in hand. Absent = no set is open (the exercise
   *  card), and every row renders as pure information. */
  onCopySet?: (patch: HistoryCopyPatch) => void;
  /** Section 1. Absent/empty = no set in hand, or nothing was computed. */
  candidates?: WeightCandidateRow[];
  locale: string;
  unit: WeightUnit;
  theme: Theme;
  getExerciseNameBySlug: (slug: string, fallback?: string) => string;
  t: (k: string, opts?: Record<string, unknown>) => string;
  /** The athlete's own scale. Leonardo logs in RPE, so a hard-coded RIR here
   *  would state his effort in the opposite number. Optional with a canonical
   *  default so an older mount cannot fail to render. */
  effortScale?: EffortScale;
  /** Whether to offer the blink switch in the history header. ⛔ Default
   *  FALSE, and it is not a style choice: that switch governs the pulse of the
   *  LOGGER'S CLOCK, and on the exercise card there is no clock — Antenore saw
   *  it there on the first APK and called it out («ha un blink on che è fuori
   *  luogo lì, no?»). Mounting a whole panel somewhere new means asking which
   *  of its parts still mean anything in that place. */
  showBlinkToggle?: boolean;
  /** How many past sets section 2 lists. His ask on 2026-09-02 was "more
   *  sessions"; the surfaces differ in how much room they have, so the number
   *  is the mount's call and not a constant in here. */
  rowLimit?: number;
  /** The athlete's current MAX for this exercise (`PG-11`), for the header
   *  control's age cue. Absent = they have none yet, which is not an age. */
  max?: MaxForProposal;
  /** Open the MAX editor for this exercise. ⛔ Absent = the header does not
   *  render: a mount with no way to write a MAX must not show the control. */
  onEditMax?: () => void;
  /** Injectable clock for the MAX age cue; real mounts omit it. */
  nowMs?: number;
};

export function HistoryPanel({
  exerciseId,
  onCopy,
  onCopySet,
  candidates,
  locale,
  unit,
  theme,
  getExerciseNameBySlug,
  t,
  effortScale = 'rir',
  showBlinkToggle = false,
  rowLimit,
  max,
  onEditMax,
  nowMs,
}: Props) {
  const loadable = useRoundingContext();
  const proposalCtx = useMemo(
    () => ({
      unit,
      ...(loadable ? { loadable } : {}),
      ...(nowMs !== undefined ? { nowMs } : {}),
    }),
    [unit, loadable, nowMs],
  );
  const { data, rows, resolved, loading } = useExerciseHistoryView(
    exerciseId,
    undefined,
    rowLimit,
    proposalCtx,
  );
  const pulse = useExerciseAgePulseEnabled();

  if (data === null) return null;
  const styles = makeStyles(theme);

  const copyLabel = t('mobile.logger.historyCopy', { defaultValue: 'Copy' });
  const copySetA11y = t('mobile.logger.historyCopySetA11y', {
    defaultValue: "Copy this set onto the set you're on",
  });
  const computed = candidates ?? [];
  // His rule: the only section open is the one holding the value in use. With
  // nothing computed, that is the history — which is what this widget was
  // before it grew sections, so the athlete who opens it finds what they
  // always found.
  const computedOpen = computed.length > 0;
  // Only the athlete's own history has a decided copy; the similar-exercise
  // anchor is computed but not rendered.
  // No proposals until the rounding context has loaded: before that the
  // weights are unrounded and would flash a value the athlete then sees change.
  const ownProposals = loadable
    ? data.proposals.filter((p) => p.source.kind === 'exercise')
    : [];

  const copyOfSet = onCopySet
    ? (row: HistorySetRow) => () =>
        onCopySet({
          weightKg: row.weightKg,
          reps: row.reps,
        })
    : null;

  return (
    <View style={styles.sections}>
      <HistoryWidgetHeader
        exerciseId={exerciseId}
        max={max}
        {...(onEditMax ? { onEditMax } : {})}
        unit={unit}
        locale={locale}
        theme={theme}
        t={t}
        {...(nowMs !== undefined ? { nowMs } : {})}
      />
      {computed.length > 0 || ownProposals.length > 0 ? (
        <WeightCandidateSection
          rows={computed}
          onUse={onCopySet ? (kg) => onCopySet({ weightKg: kg }) : undefined}
          proposals={ownProposals}
          onCopyProposal={onCopySet}
          getExerciseNameBySlug={getExerciseNameBySlug}
          copyLabel={copyLabel}
          locale={locale}
          unit={unit}
          styles={styles}
          theme={theme}
          t={t}
        />
      ) : null}

      <DisclosureCard
        theme={theme}
        initialExpanded={!computedOpen}
        accessibilityLabel={t('mobile.logger.historyToggle', {
          defaultValue: 'History',
        })}
        bodyStyle={styles.rowStack}
        header={
          <View style={styles.header}>
            <Text style={styles.headerLabel}>
              {t('mobile.logger.historyToggle', { defaultValue: 'History' })}
            </Text>
            {rows.length > 0 ? (
              <Text style={styles.headerCount}>
                {t('mobile.logger.historySetCount', {
                  defaultValue: 'last {{n}} sets',
                  n: rows.length,
                  count: rows.length,
                })}
              </Text>
            ) : null}
            {showBlinkToggle ? (
              <BlinkToggle pulse={pulse} styles={styles} t={t} />
            ) : null}
          </View>
        }
      >
        <HistoryBody
          rows={rows}
          data={data}
          resolved={resolved}
          loading={loading}
          locale={locale}
          unit={unit}
          effortScale={effortScale}
          styles={styles}
          theme={theme}
          t={t}
          copyLabel={copyLabel}
          copySetA11y={copySetA11y}
          copyOfSet={copyOfSet}
          onCopy={onCopy}
        />
      </DisclosureCard>

      {/* ⛔ Rendered even when it has nothing to say: an athlete who opens it
          and reads "nothing near enough" learns something, while a section
          that silently disappears teaches them it was never there. */}
      <DisclosureCard
        theme={theme}
        accessibilityLabel={t('mobile.logger.historyEstimateHeader', {
          defaultValue: 'Estimate from a similar exercise',
        })}
        bodyStyle={styles.rowStack}
        header={
          <Text style={styles.headerLabel}>
            {t('mobile.logger.historyEstimateHeader', {
              defaultValue: 'Estimate from a similar exercise',
            })}
          </Text>
        }
      >
        {data.estimate.candidates.length === 0 ? (
          <Text style={styles.emptyMsg}>
            {t('mobile.logger.historyNoEstimate', {
              defaultValue:
                'No history yet, and no similar exercise to estimate from.',
            })}
          </Text>
        ) : (
          data.estimate.candidates.map((c) => (
            <EstimateBlock
              key={c.exerciseId}
              session={c.session}
              sourceName={getExerciseNameBySlug(c.exerciseId)}
              locale={locale}
              unit={unit}
              effortScale={effortScale}
              styles={styles}
              theme={theme}
              t={t}
              copyLabel={copyLabel}
              copySetA11y={copySetA11y}
              copyOfSet={copyOfSet}
            />
          ))
        )}
      </DisclosureCard>
    </View>
  );
}

// The switch Antenore asked for in the same breath as the blink: the setting
// reachable from the thing it governs. It flips the SAME preference the
// Settings row does — one setting, two doors, so they cannot drift.
// ⛔ NOT a <Chip>: that was the first build, and the bench showed why it was
// wrong (a brand fill made a settings shortcut the loudest object on the
// panel). This follows `EffortNudgeOptOut` instead: quiet ink2/sm, ≥44pt, and
// THE STATE IS THE WORD, so nothing here is carried by colour.
function BlinkToggle({
  pulse,
  styles,
  t,
}: {
  pulse: ReturnType<typeof useExerciseAgePulseEnabled>;
  styles: HistoryPanelStyles;
  t: Props['t'];
}) {
  const word = t(
    pulse.enabled
      ? 'mobile.logger.historyBlinkOn'
      : 'mobile.logger.historyBlinkOff',
    { defaultValue: pulse.enabled ? 'Blink on' : 'Blink off' },
  );
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={word}
      accessibilityHint={t('mobile.logger.historyBlinkToggle', {
        defaultValue: 'Blink when an exercise has not been done in a while',
      })}
      onPress={() => pulse.setEnabled(!pulse.enabled)}
      hitSlop={8}
      style={({ pressed }) => [
        styles.blinkToggle,
        pressed && styles.blinkTogglePressed,
      ]}
    >
      <Text style={styles.blinkToggleText} numberOfLines={1}>
        {word}
      </Text>
    </Pressable>
  );
}

// Section 2's body: the sets, the all-time best, and the whole-session copy.
function HistoryBody({
  rows,
  data,
  resolved,
  loading,
  locale,
  unit,
  effortScale,
  styles,
  theme,
  t,
  copyLabel,
  copySetA11y,
  copyOfSet,
  onCopy,
}: {
  rows: HistorySetRow[];
  data: NonNullable<ReturnType<typeof useExerciseHistoryView>['data']>;
  resolved: boolean;
  loading: boolean;
  locale: string;
  unit: WeightUnit;
  effortScale: EffortScale;
  styles: HistoryPanelStyles;
  theme: Theme;
  t: Props['t'];
  copyLabel: string;
  copySetA11y: string;
  copyOfSet: ((row: HistorySetRow) => () => void) | null;
  onCopy?: Props['onCopy'];
}) {
  if (rows.length === 0) {
    // ⛔ Nothing found — but is that an ANSWER? Until the index door has
    // replied, "no history" is a guess, and it is exactly the guess that made
    // this panel lie. Say what is true instead: we are still looking.
    return (
      <Text style={styles.emptyMsg}>
        {resolved
          ? t('mobile.logger.historyNone', {
              defaultValue: "You haven't logged this exercise yet.",
            })
          : loading
            ? t('mobile.logger.historyLooking', {
                defaultValue: 'Looking up this exercise…',
              })
            : t('mobile.logger.historyUnavailable', {
                defaultValue:
                  "Can't reach your history right now. This doesn't mean there is none.",
              })}
      </Text>
    );
  }
  // Copy stays anchored to a SESSION here — it pastes a whole past entry into
  // the current one, which a single set cannot express. Offered only when the
  // rolling window supplied the data, because the per-exercise index returns
  // sets, not assembled entries. The per-ROW copy above has no such limit.
  const copySource = data.recent[0]?.entry ?? null;
  // `data.best` is already null when the best session sits inside `recent`
  // (pickHistory's rule), so this never repeats a line shown above.
  const bestRow = data.best ? (rowsFromSessions([data.best], 1)[0] ?? null) : null;
  return (
    <>
      {rows.map((row, idx) => (
        <HistoryCopyRow
          key={`${row.atMs}-${idx}`}
          styles={styles}
          theme={theme}
          copyLabel={copyLabel}
          copyA11yLabel={copySetA11y}
          onCopy={copyOfSet ? copyOfSet(row) : undefined}
        >
          <HistorySetLine
            row={row}
            locale={locale}
            unit={unit}
            effortScale={effortScale}
            styles={styles}
            t={t}
          />
        </HistoryCopyRow>
      ))}
      {/* The all-time best, when it is not already among the rows above.
          ⛔ It survives every rewrite deliberately: it is a reference, never
          a target — the proposal is anchored on the most recent session. */}
      {bestRow ? (
        <View style={styles.bestBlock}>
          <Text style={styles.bestBadge}>
            {t('mobile.logger.historyBest', { defaultValue: 'Best' })}
          </Text>
          <HistoryCopyRow
            styles={styles}
            theme={theme}
            copyLabel={copyLabel}
            copyA11yLabel={copySetA11y}
            onCopy={copyOfSet ? copyOfSet(bestRow) : undefined}
          >
            <HistorySetLine
              row={bestRow}
              locale={locale}
              unit={unit}
              effortScale={effortScale}
              styles={styles}
              t={t}
            />
          </HistoryCopyRow>
        </View>
      ) : null}
      {copySource && onCopy ? (
        <Button
          theme={theme}
          variant="secondary"
          full
          label={t('mobile.logger.historyCopyLast', {
            defaultValue: 'Copy the last session',
          })}
          onPress={() => onCopy(copySource)}
        />
      ) : null}
    </>
  );
}
